import {
  ButtonPro,
  DatePickerPro,
  Input,
  InputPro,
  Select,
  TextAreaPro,
  TextAreaProControlled,
  Typography,
  VehicleListEditor
} from 'components';
import { Form, Formik, useFormikContext } from 'formik';
import {
  ApiResponseStatus,
  GenderTypes,
  MaritalStatus,
  UserRoleNames,
  UserRoles
} from 'models/enums';
import { AdminEmployeeInfo, Project } from 'models/models';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { createEmployee, updateEmployee, getAdminEmployeeInfo } from 'store/Admin';
import {
  getDesignations,
  getEmployeesUpns,
  createDesignation,
  getProjects,
  getEmployees
} from 'store/MasterData';
import * as Yup from 'yup';
import './index.scss';

const dateFormat = 'YYYY-MM-DD';

const statusOptions = [
  { value: 'Probation', label: 'Probation' },
  { value: 'Confirmed', label: 'Confirmed' },
  { value: 'Internship', label: 'Internship' },
  { value: 'Contract', label: 'Contract' }
];

const userRoles = Object.keys(UserRoleNames).map((key) => ({
  value: key,
  label: UserRoleNames[key as keyof typeof UserRoleNames]
}));

const maritalStatus = Object.keys(MaritalStatus).map((key) => ({
  value: key,
  label: MaritalStatus[key as keyof typeof MaritalStatus]
}));

const genderType = Object.keys(GenderTypes).map((key) => ({
  value: key,
  label: GenderTypes[key as keyof typeof GenderTypes]
}));

const employeeActiveStatus = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' }
];

const terminatedByOptions = [{ value: 'HR', label: 'HR' }];

const initialState: AdminEmployeeInfo & {
  selectedProjectId?: string;
  projectStartDate?: Date | null;
  projectEndDate?: Date | null;
} = {
  userRoleId: '',
  role: '',
  upn: '',
  userName: '',
  employeeInfoId: '',
  employeeNameWithInitials: '',
  employeeFullName: '',
  employeeNickName: '',
  epfNumber: '',
  mobileNumber: '',
  nic: '',
  passportNumber: '',
  dateOfBirth: null,
  residentialPhoneNumber: '',
  emergencyContactPerson: '',
  emergencyContactPersonPhoneNumber: '',
  emergencyContactPersonRelationship: '',
  personalEmail: '',
  houseNumber: '',
  address: '',
  district: '',
  province: '',
  temporyAddress: '',
  religion: '',
  nationality: '',
  maritalStatus: '',
  gender: '',
  employmentInfoId: '',
  joinedDate: null,
  status: '',
  designationDate: null,
  designationId: '',
  designationName: '',
  confirmationDate: null,
  confirmedBy: '',
  terminationDate: null,
  terminatedBy: '',
  terminatedReason: '',
  annualLeaveCount: 0,
  casualLeaveCount: 0,
  medicalLeaveCount: 0,
  liueLeaveCount: 0,
  noPayLeaveCount: 0,
  remarks: '',
  isActive: 'true',
  designation: '',
  employeeExtensions: [],
  nameOfTheTeamLead: '',
  projectManager: '',
  techStack: '',
  employeeProjects: [],
  projectAssignments: [],
  mentorAssignment: null,
  selectedProjectId: '', // Track selected project for assignments
  projectStartDate: null, // Project start date
  projectEndDate: null // Project end date (optional)
};

const ScrollToError = () => {
  const { isSubmitting, isValid } = useFormikContext();

  useEffect(() => {
    if (isSubmitting && !isValid) {
      const errorElement = document.querySelector(
        '.input-error'
      ) as HTMLElement;
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus({ preventScroll: true });
      }
    }
  }, [isSubmitting, isValid]);

  return null;
};

export const AddEmployeeForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { id: selectedEmployeeId } = useParams();

  const isEditMode =
    !!selectedEmployeeId &&
    location.pathname === `/admin/edit-employee/${selectedEmployeeId}`;

  const [initialValues, setInitialValues] = useState(initialState);

  const isAdmin = useAppSelector(
    (state: RootState) =>
      state.auth.user.authData?.data?.userRole === UserRoles.ADMIN
  );

  const selectedEmployee = useAppSelector((state: RootState) => {
    return selectedEmployeeId
      ? state.admin.employeeData.find(
          (employee) => employee.userRoleId === selectedEmployeeId
        )
      : null;
  });

  const {
    createUserStatus: createEmployeeStatus,
    updateUserStatus: updateEmployeeStatus
  } = useAppSelector((state: RootState) => state.admin);

  const { designations, employeeUpns, projects, employees } = useAppSelector(
    (state: RootState) => {
      return state.masterData;
    }
  );

  useEffect(() => {
    dispatch(getProjects());
    dispatch(getEmployees()); // Fetch employees for dropdowns
  }, [dispatch]);

  const validationSchema = Yup.object({
    employeeFullName: Yup.string().max(100).required('Required'),
    employeeNameWithInitials: Yup.string().max(50).required('Required'),
    employeeNickName: Yup.string().max(50).required('Required'),
    dateOfBirth: Yup.date().required('Required'),
    gender: Yup.string().required('Required'),
    nic: Yup.string().max(12).required('Required'),
    passportNumber: Yup.string().max(36).nullable(),
    mobileNumber: Yup.string()
      .max(10)
      .required('Required')
      .matches(/^\+?[0-9]+$/, 'Must be only digits'),
    residentialPhoneNumber: Yup.string().max(10).nullable(),
    personalEmail: Yup.string().max(40).nullable(),
    religion: Yup.string().required('Required'),
    nationality: Yup.string().required('Required'),
    maritalStatus: Yup.string().required('Required'),
    upn: Yup.string()
      .max(45)
      .email('Invalid email address')
      .required('Required'),
    houseNumber: Yup.string().max(10).nullable(),
    address: Yup.string().required('Required'),
    district: Yup.string().max(15).nullable(),
    province: Yup.string().max(15).nullable(),
    epfNumber: Yup.string().max(10).required('Required'),
    role: Yup.string().max(50).required('Required'),
    designation: Yup.string().required('Required'),
    joinedDate: Yup.date().required('Required'),
    confirmationDate: Yup.date().required('Required'),
    status: Yup.string().max(15).required('Required'),
    techStack: Yup.string().required('Required'),
    annualLeaveCount: Yup.number()
      .required('Required')
      .typeError('Must be a number'),
    casualLeaveCount: Yup.number()
      .required('Required')
      .typeError('Must be a number'),
    medicalLeaveCount: Yup.number()
      .required('Required')
      .typeError('Must be a number'),
    liueLeaveCount: Yup.number()
      .required('Required')
      .typeError('Must be a number'),
    noPayLeaveCount: Yup.number()
      .required('Required')
      .typeError('Must be a number'),
    emergencyContactPerson: Yup.string().max(25).required('Required'),
    emergencyContactPersonPhoneNumber: Yup.string()
      .max(10)
      .required('Required')
      .matches(/^\+?[0-9]+$/, 'Must be only digits'),
    emergencyContactPersonRelationship: Yup.string()
      .max(15)
      .required('Required'),
    isActive: Yup.boolean().required('Required'),
    nameOfTheTeamLead: Yup.string().max(45).nullable(),
    projectManager: Yup.string().max(45).nullable(),
    selectedProjectId: Yup.string().nullable(), // Optional project selection
    projectStartDate: Yup.date()
      .nullable()
      .when('selectedProjectId', {
        is: (value: string) => value && value.trim() !== '',
        then: (schema) =>
          schema.required(
            'Project start date is required when project is selected'
          ),
        otherwise: (schema) => schema.nullable()
      })
      .test(
        'start-date-validation',
        'Project start date cannot be before joined date',
        function (value) {
          const { joinedDate } = this.parent;
          if (!value || !joinedDate) return true;
          return moment(value).isSameOrAfter(moment(joinedDate));
        }
      ),
    projectEndDate: Yup.date()
      .nullable()
      .test(
        'end-date-validation',
        'Project end date cannot be before start date',
        function (value) {
          const { projectStartDate } = this.parent;
          if (!value || !projectStartDate) return true;
          return moment(value).isSameOrAfter(moment(projectStartDate));
        }
      )
  });

  const convertToBoolean = (value: string | boolean) => {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return Boolean(value);
  };

  const handleSubmit = (
    values: AdminEmployeeInfo & {
      religion?: string;
      nationality?: string;
      maritalStatus?: string;
      gender?: string;
      techStack?: string;
      selectedProjectId?: string;
      projectStartDate?: Date | null;
      projectEndDate?: Date | null;
    }
  ) => {
    const formattedDate = moment().format('YYYY-MM-DD');

    // Create project assignments if a project is selected
    const projectAssignments = [];

    if (selectedEmployee) {
      // EDITING MODE: Handle project reassignment
      const existingAssignments = selectedEmployee.employeeProjects || [];
      const currentActiveAssignment = existingAssignments.find(
        (assignment: Project) => assignment.isCurrent
      );

      if (values.selectedProjectId) {
        // Check if project changed
        const projectChanged =
          !currentActiveAssignment ||
          currentActiveAssignment.projectId !== values.selectedProjectId;

        if (projectChanged) {
          // End current assignment if exists
          if (currentActiveAssignment) {
            projectAssignments.push({
              ...currentActiveAssignment,
              employeeId:
                currentActiveAssignment.employeeId ||
                selectedEmployee?.employeeInfoId ||
                '',
              isCurrent: false,
              endDate: formattedDate, // End today
              notes:
                (currentActiveAssignment.notes || '') +
                ` - Assignment ended on ${moment().format(
                  'MMMM-YYYY'
                )} due to project reassignment`
            });
          }

          // Create new assignment
          const selectedProject = projects.find(
            (p) => p.id === values.selectedProjectId
          );
          if (selectedProject) {
            const selectedDesignation = designations.find(
              (d) => d.id === values.designation
            );
            const roleInProject =
              selectedDesignation?.name || values.techStack || 'Developer';

            const previousProjectName =
              currentActiveAssignment?.projects?.name || 'None';

            // Use custom project start date or default to today
            const projectStartDate = values.projectStartDate
              ? moment(values.projectStartDate).format(dateFormat)
              : formattedDate;

            // Use custom project end date if provided
            const projectEndDate = values.projectEndDate
              ? moment(values.projectEndDate).format(dateFormat)
              : null;

            projectAssignments.push({
              id: '', // New assignment - backend will generate ID
              employeeId: selectedEmployee?.employeeInfoId || '', // Add employeeId for existing employees
              projectId: values.selectedProjectId,
              allocationPercentage: 100,
              startDate: projectStartDate,
              endDate: projectEndDate,
              isCurrent: !projectEndDate, // Only current if no end date
              roleInProject: roleInProject,
              notes: `Project reassignment from "${previousProjectName}" - Started ${moment(
                projectStartDate
              ).format('MMMM-YYYY')}${
                projectEndDate
                  ? ` - Ended ${moment(projectEndDate).format('MMMM-YYYY')}`
                  : ''
              }`,
              projects: {
                id: selectedProject.id,
                name: selectedProject.name,
                description: selectedProject.description,
                startDate: selectedProject.startDate,
                endDate: selectedProject.endDate,
                isActive: selectedProject.isActive
              }
            });
          }
        } else {
          // Same project - keep existing assignments but update role if designation changed
          const selectedDesignation = designations.find(
            (d) => d.id === values.designation
          );
          const newRoleInProject =
            selectedDesignation?.name || values.techStack || 'Developer';

          const roleChanged =
            currentActiveAssignment.roleInProject !== newRoleInProject;

          projectAssignments.push({
            ...currentActiveAssignment,
            employeeId:
              currentActiveAssignment.employeeId ||
              selectedEmployee?.employeeInfoId ||
              '',
            roleInProject: newRoleInProject,
            notes:
              currentActiveAssignment.notes +
              (roleChanged
                ? ` - Role updated to "${newRoleInProject}" in ${moment().format(
                    'MMMM-YYYY'
                  )}`
                : '')
          });
        }
      } else {
        // No project selected - end current assignment
        if (currentActiveAssignment) {
          projectAssignments.push({
            ...currentActiveAssignment,
            employeeId:
              currentActiveAssignment.employeeId ||
              selectedEmployee?.employeeInfoId ||
              '',
            isCurrent: false,
            endDate: formattedDate,
            notes:
              (currentActiveAssignment.notes || '') +
              ` - Assignment ended on ${moment().format(
                'MMMM-YYYY'
              )} - No project assigned`
          });
        }
      }

      // Add any other non-current assignments unchanged
      const otherAssignments = existingAssignments.filter(
        (assignment: Project) => !assignment.isCurrent
      );
      projectAssignments.push(...otherAssignments);
    } else {
      // CREATION MODE: Create initial assignment
      if (values.selectedProjectId) {
        const selectedProject = projects.find(
          (p) => p.id === values.selectedProjectId
        );
        if (selectedProject) {
          const selectedDesignation = designations.find(
            (d) => d.id === values.designation
          );
          const roleInProject =
            selectedDesignation?.name || values.techStack || 'Developer';

          // Use custom project start date or fallback to joined date or today
          const projectStartDate = values.projectStartDate
            ? moment(values.projectStartDate).format(dateFormat)
            : values.joinedDate
            ? moment(values.joinedDate).format(dateFormat)
            : formattedDate;

          // Use custom project end date if provided
          const projectEndDate = values.projectEndDate
            ? moment(values.projectEndDate).format(dateFormat)
            : null;

          projectAssignments.push({
            id: '', // Will be generated by backend
            employeeId: '', // Will be set by backend for new employees
            projectId: values.selectedProjectId,
            allocationPercentage: 100, // Default 100% allocation for new employees
            startDate: projectStartDate,
            endDate: projectEndDate,
            isCurrent: !projectEndDate, // Only current if no end date
            roleInProject: roleInProject, // Uses designation name or tech stack
            notes: `Initial assignment to "${
              selectedProject.name
            }" - Started ${moment(projectStartDate).format('MMMM-YYYY')}${
              projectEndDate
                ? ` - Ended ${moment(projectEndDate).format('MMMM-YYYY')}`
                : ''
            }`,
            projects: {
              id: selectedProject.id,
              name: selectedProject.name,
              description: selectedProject.description,
              startDate: selectedProject.startDate,
              endDate: selectedProject.endDate,
              isActive: selectedProject.isActive
            }
          });
        }
      }
    }

    // Create mentor assignments from form values (PM and TL names)
    const mentorAssignment = {
      id: selectedEmployee?.mentorAssignment?.id || '', // Preserve existing ID for updates, empty for new employees
      mentor01Id: '', // Project Manager ID
      mentor02Id: '', // Technical Lead ID
      mentor03Id: '' // Additional mentor ID (not used currently but required by API)
    };

    // Set mentors based on form values (now using employeeId directly from dropdowns)
    if (values.projectManager && values.projectManager.trim() !== '') {
      mentorAssignment.mentor01Id = values.projectManager; // employeeId from dropdown
    }

    if (values.nameOfTheTeamLead && values.nameOfTheTeamLead.trim() !== '') {
      mentorAssignment.mentor02Id = values.nameOfTheTeamLead; // employeeId from dropdown
    }

    // If project is selected, fallback to project's default PM/TL if form fields are empty
    if (values.selectedProjectId) {
      const selectedProject = projects.find(
        (p) => p.id === values.selectedProjectId
      );
      if (selectedProject) {
        // Fallback to project's PM if no PM specified
        if (!mentorAssignment.mentor01Id && selectedProject.projectManagerId) {
          mentorAssignment.mentor01Id = selectedProject.projectManagerId;
        }

        // Fallback to project's TL if no TL specified
        if (!mentorAssignment.mentor02Id && selectedProject.technicalLeadId) {
          mentorAssignment.mentor02Id = selectedProject.technicalLeadId;
        }
      }
    }

    const formattedValues = {
      userRoleId: values.userRoleId || '',
      role: values.role || '',
      upn: values.upn, 
      userName: values.employeeNickName || '',
      employeeInfoId: values.employeeInfoId || '',
      employeeNameWithInitials: values.employeeNameWithInitials || '',
      employeeNickName: values.employeeNickName || '',
      employeeFullName: values.employeeFullName || '',
      epfNumber: values.epfNumber || '',
      mobileNumber: values.mobileNumber || '',
      nic: values.nic || '',
      passportNumber: values.passportNumber || '',
      dateOfBirth: values.dateOfBirth
        ? moment(values.dateOfBirth).format(dateFormat)
        : null,
      residentialPhoneNumber: values.residentialPhoneNumber || '',
      emergencyContactPerson: values.emergencyContactPerson || '',
      emergencyContactPersonPhoneNumber:
        values.emergencyContactPersonPhoneNumber || '',
      emergencyContactPersonRelationship:
        values.emergencyContactPersonRelationship || '',
      personalEmail: values.personalEmail || '',
      houseNumber: values.houseNumber || '',
      address: values.address || '',
      district: values.district || '',
      province: values.province || '',
      temporyAddress: values.temporyAddress || '',
      isActive: convertToBoolean(values.isActive),
      religion: values.religion || '',
      nationality: values.nationality || '',
      maritalStatus: values.maritalStatus || '',
      gender: values.gender || '',
      employmentInfoId: values.employmentInfoId || '',
      joinedDate: values.joinedDate
        ? moment(values.joinedDate).format(dateFormat)
        : null,
      status: values.status || '',
      designation: values.designation || '', // Keep for compatibility
      designationId: values.designation || '',
      designationName: values.designationName || '',
      designationDate: values.designationDate
        ? moment(values.designationDate).format(dateFormat)
        : formattedDate,
      confirmationDate: values.confirmationDate
        ? moment(values.confirmationDate).format(dateFormat)
        : null,
      confirmedBy: values.confirmedBy || '',
      terminationDate: values.terminationDate
        ? moment(values.terminationDate).format(dateFormat)
        : null,
      terminatedBy: values.terminatedBy || '',
      terminatedReason: values.terminatedReason || '',
      nameOfTheTeamLead: values.nameOfTheTeamLead || '',
      projectManager: values.projectManager || '',
      techStack: values.techStack || '',
      annualLeaveCount: parseFloat(values.annualLeaveCount?.toString() || '0'),
      casualLeaveCount: parseFloat(values.casualLeaveCount?.toString() || '0'),
      medicalLeaveCount: parseFloat(
        values.medicalLeaveCount?.toString() || '0'
      ),
      liueLeaveCount: parseFloat(values.liueLeaveCount?.toString() || '0'),
      noPayLeaveCount: parseFloat(values.noPayLeaveCount?.toString() || '0'),
      remarks: values.remarks || '',
      employeeExtensions: [
        {
          id: '',
          userId: '',
          extensionDate: formattedDate,
          period: 0,
          extendedBy: '',
          reason: ''
        }
      ],
      employeeProjects: projectAssignments,
      projectAssignments: projectAssignments.map((assignment) => ({
        id: assignment.id,
        projectId: assignment.projectId,
        allocationPercentage: assignment.allocationPercentage,
        startDate: assignment.startDate,
        endDate: assignment.endDate,
        isCurrent: assignment.isCurrent,
        roleInProject: assignment.roleInProject,
        notes: assignment.notes
      })),
      mentorAssignment: mentorAssignment
    };

    if (formattedValues.status !== 'Terminated') {
      formattedValues.terminationDate = null;
      formattedValues.terminatedBy = '';
      formattedValues.terminatedReason = '';
    } else {
      // If employee is terminated, update employeeProjects
      if (formattedValues.employeeProjects.length > 0) {
        formattedValues.employeeProjects = formattedValues.employeeProjects.map(
          (assignment: Project) => ({
            ...assignment,
            isCurrent: false,
            endDate: formattedValues.terminationDate || formattedDate,
            notes: assignment.notes + ' - Assignment ended due to termination'
          })
        );
      }
    }

    if (selectedEmployee) {
      dispatch(updateEmployee(formattedValues)).then((response) => {
        if (response.meta.requestStatus === 'fulfilled') {
          navigate(`/admin`);
        }
      });
    } else {
      dispatch(createEmployee(formattedValues)).then((response) => {
        if (response.meta.requestStatus === 'fulfilled') {
          navigate(`/admin`);
        }
      });
    }
  };

  const getStatusOptions = () => {
    if (selectedEmployee) {
      return [
        ...statusOptions,
        { value: 'Terminated', label: 'Terminated' },
        { value: 'Resigned', label: 'Resigned' }
      ];
    }
    return statusOptions;
  };

  useEffect(() => {
    if (
      selectedEmployeeId &&
      location.pathname === `/admin/edit-employee/${selectedEmployeeId}`
    ) {
      dispatch(getAdminEmployeeInfo());
    }
  }, [dispatch, selectedEmployeeId, location.pathname]);

  useEffect(() => {
    if (
      selectedEmployeeId &&
      location.pathname === `/admin/edit-employee/${selectedEmployeeId}`
    ) {
      if (selectedEmployee) {
        // Find current active project assignment
        const currentAssignment = selectedEmployee.employeeProjects?.find(
          (assignment: Project) => assignment.isCurrent
        );
        const currentProjectId = currentAssignment?.projectId || '';

        setInitialValues({
          ...selectedEmployee,
          designation:
            selectedEmployee.designationId ||
            selectedEmployee.designation ||
            '',
          selectedProjectId: currentProjectId,
          projectStartDate: currentAssignment?.startDate
            ? new Date(currentAssignment.startDate)
            : null,
          projectEndDate: currentAssignment?.endDate
            ? new Date(currentAssignment.endDate)
            : null,
          projectManager:
            selectedEmployee.mentorAssignment?.mentor01Id || '',
          nameOfTheTeamLead:
            selectedEmployee.mentorAssignment?.mentor02Id || ''
        });
      }
    } else {
      setInitialValues(initialState);
    }
  }, [selectedEmployeeId, location.pathname, selectedEmployee]);

  // State for modal and new designation
  const [showDesignationModal, setShowDesignationModal] = useState(false);
  const [newDesignationName, setNewDesignationName] = useState('');
  const [newDesignationDesc, setNewDesignationDesc] = useState('');
  const [designationError, setDesignationError] = useState('');

  useEffect(() => {
    if (designations.length === 0) dispatch(getDesignations());
    if (employeeUpns.length === 0) dispatch(getEmployeesUpns());
    if (employees.length === 0) dispatch(getEmployees());
  }, [designations.length, employeeUpns.length, employees.length, dispatch]);

  const handleCreateDesignation = async () => {
    if (!newDesignationName.trim()) {
      setDesignationError('Designation name is required');
      return;
    }
    try {
      const result = await dispatch(
        createDesignation({
          name: newDesignationName,
          description: newDesignationDesc
        })
      );
      if (createDesignation.fulfilled.match(result)) {
        setShowDesignationModal(false);
        setNewDesignationName('');
        setNewDesignationDesc('');
        setDesignationError('');
      } else {
        setDesignationError('Failed to create designation');
      }
    } catch (err) {
      setDesignationError('Failed to create designation');
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        errors,
        touched,
        isValid,
        isSubmitting
      }: any) => {
        return (
          <Form>
            <div className="manage-employe-container">
              <div className="manage-employe-title">
                <Typography
                  text="Employee registration"
                  type="base_regular"
                  weight="bold"
                />
              </div>

              <div className="manage-employe-form">
                <div className="registration-step-wrapper">
                  <div className="step-number">1</div>
                  <div className="step-info">
                    <Typography
                      text="Personal Information"
                      type="base_regular"
                      weight="bold"
                    />
                    <Typography
                      text="Enter basic details about the employee"
                      type="small_medium"
                      color="gray"
                    />
                  </div>
                </div>

                <div className="registration-form">
                  <Input
                    label="Full name"
                    name="employeeFullName"
                    placeholder="Enter full name"
                    inputWrapperClass="name-input-wrapper margin-24"
                    required
                  />
                  <Input
                    label="Name with initials"
                    name="employeeNameWithInitials"
                    placeholder="Enter name with initials"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                  <Input
                    label="Employee calling name"
                    name="employeeNickName"
                    placeholder="Enter employee calling name"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                  <Select
                    label="Active status"
                    name="isActive"
                    placeholder="Select employee active status"
                    inputWrapperClass="width-half margin-24"
                    required
                    options={employeeActiveStatus}
                  />
                  <DatePickerPro
                    label="Date of birth"
                    name="dateOfBirth"
                    required
                    inputWrapperClass="width-half margin-24"
                    maxDate={new Date()}
                  />
                  <Select
                    label="Gender"
                    name="gender"
                    placeholder="Select gender"
                    inputWrapperClass="width-half margin-24"
                    required
                    options={genderType}
                  />
                  <Input
                    label="NIC number"
                    name="nic"
                    placeholder="Enter nic number"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                  <Input
                    label="Passport number"
                    name="passportNumber"
                    placeholder="Enter passport number"
                    inputWrapperClass="width-half margin-24"
                  />
                  <Input
                    label="Mobile number"
                    name="mobileNumber"
                    placeholder="Enter mobile number"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                  <Input
                    label="Residencial phone number"
                    name="residentialPhoneNumber"
                    placeholder="Enter residencial phone number"
                    inputWrapperClass="width-half margin-24"
                  />
                  <Input
                    label="Personal email address"
                    name="personalEmail"
                    placeholder="Enter personal email address"
                    inputWrapperClass="width-half margin-24"
                  />
                  <Input
                    label="Religion"
                    name="religion"
                    placeholder="Enter religion"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                  <Input
                    label="Nationality"
                    name="nationality"
                    placeholder="Enter nationality"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                  <Select
                    label="Marital status"
                    name="maritalStatus"
                    placeholder="Select marital status"
                    inputWrapperClass="width-half margin-24"
                    required
                    options={maritalStatus}
                  />
                  <Input
                    label="UPN"
                    name="upn"
                    placeholder="Enter upn"
                    inputWrapperClass="width-half margin-24"
                    disabled={selectedEmployee ? true : false}
                    required
                  />
                  <Select
                    label="User role"
                    name="role"
                    placeholder="Select user role"
                    inputWrapperClass="width-half margin-24"
                    required
                    options={userRoles}
                  />
                  <Input
                    label="House number"
                    name="houseNumber"
                    placeholder="Enter house number"
                    inputWrapperClass="width-half margin-24"
                  />
                  <Input
                    label="Address"
                    name="address"
                    placeholder="Enter address"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                  <Input
                    label="District"
                    name="district"
                    placeholder="Enter district"
                    inputWrapperClass="width-half margin-24"
                  />
                  <Input
                    label="Province"
                    name="province"
                    placeholder="Enter province"
                    inputWrapperClass="width-half margin-24"
                  />
                </div>
              </div>

              <div className="manage-employe-form">
                <div className="registration-step-wrapper">
                  <div className="step-number">2</div>
                  <div className="step-info">
                    <Typography
                      text="Employment Information"
                      type="base_regular"
                      weight="bold"
                    />
                    <Typography
                      text="Provide employment details for the employee"
                      type="small_medium"
                      color="gray"
                    />
                  </div>
                </div>

                <div className="registration-form">
                  <Input
                    label="Employee ID"
                    name="epfNumber"
                    placeholder="Enter employee ID"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <Select
                      label="Designation"
                      name="designation"
                      placeholder="Select employee designation"
                      inputWrapperClass="width-half margin-24"
                      required
                      options={designations.map((designation) => ({
                        value: designation.id,
                        label: designation.name
                      }))}
                    />
                    <ButtonPro
                      type="primary"
                      htmlType="button"
                      label="Add new designation"
                      onClick={() => setShowDesignationModal(true)}
                    />
                  </div>

                  {/* Display selected designation description */}
                  {values.designation && (
                    <div
                      style={{
                        marginBottom: '24px',
                        padding: '4px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '4px',
                        width: '70%'
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          color: '#495057',
                          fontSize: '10px'
                        }}
                      >
                        Designation Description:
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          color: '#6c757d',
                          lineHeight: '1.4'
                        }}
                      >
                        {(() => {
                          const selectedDesignation = designations.find(
                            (d) => d.id === values.designation
                          );
                          return (
                            selectedDesignation?.description ||
                            'No description available'
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Modal for creating new designation */}
                  {showDesignationModal && (
                    <div className="modal-overlay">
                      <div className="modal-content">
                        <Typography
                          type="heading5"
                          text="Create New Designation"
                        />
                        <InputPro
                          label="Designation Name"
                          name="designationName"
                          placeholder="Enter designation name"
                          value={newDesignationName}
                          onChange={setNewDesignationName}
                          inputWrapperClass="margin-24"
                          required
                        />
                        <TextAreaProControlled
                          label="Description"
                          name="designationDescription"
                          placeholder="Enter description (optional)"
                          value={newDesignationDesc}
                          onChange={setNewDesignationDesc}
                          inputWrapperClass="margin-24"
                        />
                        {designationError && (
                          <div style={{ color: 'red', marginBottom: 8 }}>
                            {designationError}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 12 }}>
                          <ButtonPro
                            label="Cancel"
                            type="white"
                            htmlType="button"
                            onClick={() => {
                              setShowDesignationModal(false);
                              setDesignationError('');
                            }}
                          />
                          <ButtonPro
                            label="Create"
                            type="primary"
                            htmlType="button"
                            onClick={handleCreateDesignation}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <DatePickerPro
                    label="Joined date"
                    name="joinedDate"
                    required
                    inputWrapperClass="width-half margin-24"
                    maxDate={new Date()}
                  />
                  <DatePickerPro
                    label="Confirmation due date"
                    name="confirmationDate"
                    inputWrapperClass="width-half margin-24"
                  />
                  <Select
                    label="Status"
                    name="status"
                    placeholder="Enter status"
                    inputWrapperClass="width-half margin-24"
                    required
                    options={getStatusOptions()}
                  />
                  <Input
                    label="Tech Stack"
                    name="techStack"
                    placeholder="Enter tech stack"
                    inputWrapperClass="width-half margin-24"
                    required
                  />

                  {/* Show current project info when editing */}
                  {selectedEmployee && (
                    <div
                      className="current-project-info"
                      style={{
                        marginBottom: '24px',
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '4px',
                        width: '100%'
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          color: '#495057'
                        }}
                      >
                        Current Project Assignment:
                      </div>
                      {(() => {
                        const currentProject =
                          selectedEmployee.employeeProjects?.find(
                            (p) => p.isCurrent
                          );
                        return currentProject ? (
                          <div style={{ fontSize: '14px' }}>
                            <div>
                              <strong>Project:</strong>{' '}
                              {currentProject.projects.name}
                            </div>
                            <div>
                              <strong>Role:</strong>{' '}
                              {currentProject.roleInProject}
                            </div>
                            <div>
                              <strong>Allocation:</strong>{' '}
                              {currentProject.allocationPercentage}%
                            </div>
                            <div>
                              <strong>Started:</strong>{' '}
                              {currentProject.startDate
                                ? new Date(
                                    currentProject.startDate
                                  ).toLocaleDateString()
                                : 'N/A'}
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              fontStyle: 'italic'
                            }}
                          >
                            No active project assignment
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12
                    }}
                  >
                    <Select
                      label="Project or Team"
                      name="selectedProjectId"
                      placeholder="Select Project or Team"
                      inputWrapperClass="width-half margin-24"
                      options={projects.map((project) => ({
                        value: project.id,
                        label: `${project.name} - ${project.description}`
                      }))}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const selectedProject = projects.find(
                            (p) => p.id === selectedOption.value
                          );
                          if (selectedProject) {
                            // Find PM employee ID by name
                            const pmEmployee = employees.find(
                              (emp) =>
                                emp.employeeName ===
                                selectedProject.projectManager
                            );
                            setFieldValue(
                              'projectManager',
                              pmEmployee?.employeeId || ''
                            );

                            // Find TL employee ID by name
                            const tlEmployee = employees.find(
                              (emp) =>
                                emp.employeeName ===
                                selectedProject.technicalLead
                            );
                            setFieldValue(
                              'nameOfTheTeamLead',
                              tlEmployee?.employeeId || ''
                            );

                            // Set selected project ID for project assignments
                            setFieldValue(
                              'selectedProjectId',
                              selectedProject.id
                            );

                            // Auto-set project start date for new employees (default to joined date)
                            if (
                              !selectedEmployee &&
                              values.joinedDate &&
                              !values.projectStartDate
                            ) {
                              setFieldValue(
                                'projectStartDate',
                                values.joinedDate
                              );
                            }
                          }
                        } else {
                          // Clear fields if no project selected
                          setFieldValue('projectManager', '');
                          setFieldValue('nameOfTheTeamLead', '');
                          setFieldValue('selectedProjectId', '');
                          setFieldValue('projectStartDate', null);
                          setFieldValue('projectEndDate', null);
                        }
                      }}
                    />

                    {/* Project Date Fields - Show only when project is selected */}
                    {values.selectedProjectId && (
                      <>
                        <DatePickerPro
                          label="Project start date"
                          name="projectStartDate"
                          placeholder="Select project start date"
                          inputWrapperClass="width-half margin-24"
                          required
                        />
                        <DatePickerPro
                          label="Project end date (optional)"
                          name="projectEndDate"
                          placeholder="Select project end date (leave empty if ongoing)"
                          inputWrapperClass="width-half margin-24"
                        />
                      </>
                    )}
                  </div>
                  <Select
                    label="Technical Lead / Reporting Manager"
                    name="nameOfTheTeamLead"
                    placeholder="Select Technical Lead / Reporting Manager"
                    inputWrapperClass="width-half margin-24"
                    options={employees
                      .filter(
                        (emp) =>
                          emp.isActive ||
                          emp.employeeId === values.nameOfTheTeamLead
                      )
                      .map((emp) => ({
                        value: emp.employeeId,
                        label: emp.employeeName
                      }))}
                  />
                  <Select
                    label="Project Manager / Manager"
                    name="projectManager"
                    placeholder="Select Project Manager / Manager"
                    inputWrapperClass="width-half margin-24"
                    options={employees
                      .filter(
                        (emp) =>
                          emp.isActive ||
                          emp.employeeId === values.projectManager
                      )
                      .map((emp) => ({
                        value: emp.employeeId,
                        label: emp.employeeName
                      }))}
                  />
                  {selectedEmployee && values.status === 'Terminated' && (
                    <>
                      <DatePickerPro
                        label="Termination date"
                        name="terminationDate"
                        required
                        inputWrapperClass="width-half margin-24"
                      />
                      <Select
                        label="Terminated by"
                        name="terminatedBy"
                        placeholder="Enter terminated by"
                        inputWrapperClass="width-half margin-24"
                        required
                        options={terminatedByOptions}
                      />
                      <TextAreaPro
                        label="Termination reason"
                        name="terminatedReason"
                        placeholder="Enter termination reason"
                        inputWrapperClass="width-half margin-24"
                        required
                      />
                    </>
                  )}

                  {isEditMode && (
                    <div className="leave-allocation-edit-notice">
                      <Typography
                        text="Leave allocations are managed separately. The fields below show the current allocation for reference only. To change this employee's annual, casual, medical, lieu, or no-pay leave for the current year, use the Annual Leave Allocation page."
                        type="small_medium"
                        color="gray"
                      />
                      <button
                        type="button"
                        className="leave-allocation-link"
                        onClick={() =>
                          navigate('/admin/annual-leave-allocation')
                        }
                      >
                        Go to Annual Leave Allocation
                        <span className="arrow">→</span>
                      </button>
                    </div>
                  )}
                  
                  <Input
                    label="Annual leave count"
                    name="annualLeaveCount"
                    placeholder="Enter annual leave count"
                    inputWrapperClass="width-half margin-24"
                    required
                    disabled={isEditMode}
                  />
                  <Input
                    label="Casual leave count"
                    name="casualLeaveCount"
                    placeholder="Enter casual leave count"
                    inputWrapperClass="width-half margin-24"
                    required
                    disabled={isEditMode}
                  />
                  <Input
                    label="Medical leave count"
                    name="medicalLeaveCount"
                    placeholder="Enter medical leave count"
                    inputWrapperClass="width-half margin-24"
                    required
                    disabled={isEditMode}
                  />
                  <Input
                    label="Lieu leave count"
                    name="liueLeaveCount"
                    placeholder="Enter lieu leave count"
                    inputWrapperClass="width-half margin-24"
                    required
                    disabled={isEditMode}
                  />
                  <Input
                    label="No pay leave count"
                    name="noPayLeaveCount"
                    placeholder="Enter no pay leave count"
                    inputWrapperClass="width-half margin-24"
                    required
                    disabled={isEditMode}
                  />
                </div>
              </div>

              <div className="manage-employe-form">
                <div className="registration-step-wrapper">
                  <div className="step-number">3</div>
                  <div className="step-info">
                    <Typography
                      text="Emergency Contact"
                      type="base_regular"
                      weight="bold"
                    />
                    <Typography
                      text="Enter emergency contact details"
                      type="small_medium"
                      color="gray"
                    />
                  </div>
                </div>

                <div className="registration-form">
                  <Input
                    label="Emergency contact person"
                    name="emergencyContactPerson"
                    placeholder="Enter emergency contact person"
                    inputWrapperClass="width-half margin-24"
                    required
                  />

                  <Input
                    label="Emergency contact person number"
                    name="emergencyContactPersonPhoneNumber"
                    placeholder="Enter emergency contact person number"
                    inputWrapperClass="width-half margin-24"
                    required
                  />

                  <Input
                    label="Emergency contact person relationship"
                    name="emergencyContactPersonRelationship"
                    placeholder="Enter emergency contact person relationship"
                    inputWrapperClass="width-half margin-24"
                    required
                  />
                </div>
              </div>

              {isEditMode && isAdmin && selectedEmployee?.employeeInfoId && (
                <div className="manage-employe-form">
                  <div className="registration-step-wrapper">
                    <div className="step-number">4</div>
                    <div className="step-info">
                      <Typography
                        text="Vehicles"
                        type="base_regular"
                        weight="bold"
                      />
                      <Typography
                        text="Add or remove the vehicles this employee parks at the office. Changes save immediately."
                        type="small_medium"
                        color="gray"
                      />
                    </div>
                  </div>

                  <VehicleListEditor
                    target={{ employeeInfoId: selectedEmployee.employeeInfoId }}
                    emptyText="No vehicles added yet."
                  />
                </div>
              )}
            </div>

            <div className="form-actions">
              <ButtonPro
                type="white"
                htmlType="button"
                label="Cancel"
                onClick={() => navigate(-1)}
              />
              <ButtonPro
                type="primary"
                htmlType="submit"
                label={selectedEmployee ? 'Update' : 'Register'}
                onClick={() => {}}
                isLoading={
                  createEmployeeStatus === ApiResponseStatus.pending ||
                  updateEmployeeStatus === ApiResponseStatus.pending
                }
                disabled={
                  createEmployeeStatus === ApiResponseStatus.pending ||
                  updateEmployeeStatus === ApiResponseStatus.pending
                }
              />
            </div>

            <ScrollToError />
          </Form>
        );
      }}
    </Formik>
  );
};
